import { React, useState, useMemo } from 'react';
import { Form, Button, ToggleButtonGroup, ToggleButton } from 'react-bootstrap';
import { ChromePicker } from 'react-color';
import { Icon } from '@iconify/react';
import ColorList from './ColorList';
import { useTranslation } from 'react-i18next';
import {
  COLOR_GROUPS,
  applyColorGroupColors,
  generateRandomHexColor,
  pickMajorityKeycapColor,
} from '../../../../utils/colorHelpers';

const KeycapColorEditor = ({
  isActive,
  design,
  hasAssignedColorGroups,
  relevantGroups,
  updateDesign,
}) => {
  const { t } = useTranslation();

  const { colors, selectedColorEntry } = design.keycapColorEditor;
  const [deleteMode, setDeleteMode] = useState(false); // state to toggle delete colors mode on and off
  const keyGroups = [
    { name: t('keycap-color-editor-component-custom'), value: 'none' },
    { name: t('keycap-color-editor-component-all'), value: 'all' },
    {
      name: t('keycap-color-editor-component-function-row'),
      value: 'function',
    },
    {
      name: t('keycap-color-editor-component-alphanumerics'),
      value: 'alphanumeric',
    },
    { name: t('keycap-color-editor-component-modifiers'), value: 'modifier' },
    {
      name: t('keycap-color-editor-component-navigation'),
      value: 'navigation',
    },
    { name: t('keycap-color-editor-component-numpad'), value: 'numpad' },
  ]; // an array containing all available keycap group selectors

  const { selectedKeycaps = [], selectedEditor } = design.settings;
  const showUnselectButton =
    selectedEditor === 'colors' && selectedKeycaps.length;

  const majorityColor = useMemo(
    () => pickMajorityKeycapColor(design, selectedKeycaps, '#FFFFFF'),
    [selectedKeycaps, design.keycapData]
  ); // computes the color that is present the most across the current selectedKeycaps. Recompute when selectedKeycaps change or when any selected keycap's color changes.

  const randomizeColors = async () => {
    const colorGroupColors = {};

    COLOR_GROUPS.forEach((colorGroup) => {
      colorGroupColors[colorGroup] = generateRandomHexColor();
    }); // Assign a random hex color to each color group

    updateDesign({
      'settings.selectedKeycaps': [], // Unselect all keycaps
      keycapData: (prevKeycapData) =>
        applyColorGroupColors(prevKeycapData, colorGroupColors), // Update the keycapData with randomized colors
    });
  }; // this function applies random keycap colors, based on the different color groups (each keycap is assigned to one of three color groups, this color pattern follows a popular color design)

  const handleKeycapColorButtonClick = (index) => {
    if (deleteMode) {
      // Handle color deletion
      updateDesign((prevDesign) => {
        const deletedColorIndex = index;
        const prevSelectedEntry =
          prevDesign.keycapColorEditor.selectedColorEntry;

        const updatedColors = prevDesign.keycapColorEditor.colors.filter(
          (_, i) => i !== deletedColorIndex
        ); // Remove the color at the index

        const updatedKeycapData = prevDesign.keycapData.map((row) =>
          row.map((keycap) => {
            const selectedKeycapColorIndex = keycap?.selectedKeycapColorIndex;

            if (selectedKeycapColorIndex != null) {
              let updatedIndex = selectedKeycapColorIndex;

              if (updatedIndex === deletedColorIndex) {
                updatedIndex = null; // Reset index to null if it matches the deleted one
              } else if (updatedIndex > deletedColorIndex) {
                updatedIndex -= 1; // Adjust index if it is greater than the deleted one
              }

              return { ...keycap, selectedKeycapColorIndex: updatedIndex }; // Update the keycap
            }
            return keycap;
          })
        );

        if (updatedColors.length === 0) {
          toggleDeleteColorMode(); // If there's only one color left, exit delete mode
        }

        // update the selectedColorEntry:
        const nextSelectedEntry =
          prevSelectedEntry == null
            ? null
            : prevSelectedEntry === deletedColorIndex
              ? null // reset the selectedColorEntry to null, if the color entry gets deleted
              : prevSelectedEntry > deletedColorIndex
                ? prevSelectedEntry - 1 // if the selectedColorEntry comes after the deleted entry (inside the color list), subtract 1 from the selectedColorEntry index
                : prevSelectedEntry;

        return {
          ...prevDesign,
          keycapColorEditor: {
            ...prevDesign.keycapColorEditor,
            colors: updatedColors, // Update the colors array
            selectedColorEntry: nextSelectedEntry,
          },
          keycapData: updatedKeycapData, // Update the keycapData
        };
      });
    } else {
      if (selectedKeycaps.length > 0) {
        // if the user has selected keycaps, add/remove the reference to the color to/from all selected keycaps and apply the color to the keycaps if the refernce has been added:
        updateDesign((prevDesign) => {
          const selectedColor = prevDesign.keycapColorEditor.colors[index]; // Get the selected color from prevDesign
          const selectedKeycaps = prevDesign.settings.selectedKeycaps || [];

          const allSelectedAlreadyIndex =
            selectedKeycaps.length > 0 &&
            selectedKeycaps.every(({ rowIndex, keycapIndex }) => {
              const keycap = prevDesign.keycapData?.[rowIndex]?.[keycapIndex];
              return (
                keycap &&
                !keycap.isSpacer &&
                keycap.selectedKeycapColorIndex === index
              );
            }); // check if ALL selected keycaps already point to this palette index

          const selectedSet = new Set(
            selectedKeycaps.map(
              ({ rowIndex, keycapIndex }) => `${rowIndex}:${keycapIndex}`
            )
          ); // build a fast lookup ("row:keycap" -> selected)

          const nextKeycapData = prevDesign.keycapData.map((row, rIndex) =>
            row.map((keycap, kIndex) => {
              if (
                !selectedSet.has(`${rIndex}:${kIndex}`) ||
                !keycap ||
                keycap.isSpacer
              ) {
                return keycap; // skip non-selected, undefined, or spacer entries
              }

              if (allSelectedAlreadyIndex) {
                // if all selectedKeycaps already reference the selected color
                return {
                  ...keycap,
                  selectedKeycapColorIndex: null, // remove the reference to this color index from all selected keycaps
                  // keep keycap.color as-is
                };
              } else {
                // at least one selected keycap does not reference this index
                return {
                  ...keycap,
                  selectedKeycapColorIndex: index, // make sure all selectedKeycaps reference this color index
                  color: selectedColor, // and update the keycap color accordingly
                };
              }
            })
          );

          return {
            ...prevDesign,
            keycapData: nextKeycapData,
          };
        });
      } else {
        // No keycaps selected: toggle the palette entry
        updateDesign((prev) => {
          const curr = prev.keycapColorEditor.selectedColorEntry; // current selectedColorEntry
          const next = curr === index ? null : index; // toggling the selected entry

          return {
            ...prev,
            keycapColorEditor: {
              ...prev.keycapColorEditor,
              selectedColorEntry: next,
            },
          };
        });
      }
    }
  }; // this function handles selecting, deselecting and deleting keycap colors

  const handleAddColor = (currentColor) => {
    updateDesign((prevDesign) => {
      const newColorIndex = prevDesign.keycapColorEditor.colors.length; // Determine the index of the new color
      const selectedKeycaps = prevDesign.settings.selectedKeycaps || []; // all selected keycaps

      const selectedLookup = new Set(
        selectedKeycaps.map(
          ({ rowIndex, keycapIndex }) => `${rowIndex}:${keycapIndex}`
        )
      ); // build a fast lookup set ("row:keycap" → selected)

      const nextKeycapData = prevDesign.keycapData.map((row, rIndex) =>
        row.map((keycap, kIndex) => {
          if (
            selectedLookup.has(`${rIndex}:${kIndex}`) &&
            keycap &&
            !keycap.isSpacer
          ) {
            return {
              ...keycap,
              selectedKeycapColorIndex: newColorIndex, // select the newly added color
              color: currentColor, // also make sure that all keycaps inside the new color group have the currentColor applied
            };
          }
          return keycap; // return keycap unchanged
        })
      ); // update every keycap that is selected (skip spacers defensively)

      return {
        ...prevDesign,
        keycapColorEditor: {
          ...prevDesign.keycapColorEditor,
          colors: [...prevDesign.keycapColorEditor.colors, currentColor], // append new color
        },
        keycapData: nextKeycapData, // apply to all selected keycaps
      };
    });
  }; // adds a new color to the colors array and updates the color index of the selected keycaps (if any selected)

  const toggleDeleteColorMode = () => {
    setDeleteMode(!deleteMode);
  }; // toggling the delete color mode state

  const unselectKeycaps = () => {
    updateDesign({
      'settings.selectedKeycaps': [],
      'settings.isoEnterSelected': false,
    });
  }; // unselect all keycaps

  const handleColorChange = (color) => {
    const updatedColor = color.hex; // Get the hex value from the color

    if (selectedKeycaps.length > 0) {
      // if the user has keycaps selected:
      updateDesign((prevDesign) => {
        const selectedKeycaps = prevDesign.settings.selectedKeycaps || [];
        if (selectedKeycaps.length === 0) return prevDesign;

        const selectedSet = new Set(
          selectedKeycaps.map(
            ({ rowIndex, keycapIndex }) => `${rowIndex}:${keycapIndex}`
          )
        ); // create a fast lookup for all selected keycaps ("row:keycap")

        const updatedKeycapData = prevDesign.keycapData.map((row, rIndex) =>
          row.map((keycap, kIndex) => {
            if (!keycap || keycap.isSpacer) return keycap; // skip empty objects and spacers
            if (!selectedSet.has(`${rIndex}:${kIndex}`)) return keycap; // skip keycaps that are not selected

            return {
              ...keycap,
              color: updatedColor, // update the color of all selected keycaps
              selectedKeycapColorIndex: null, // reset the selectedKeycapColorIndex (so the keycap no longer links to a color entry from the list)
            };
          })
        );

        return {
          ...prevDesign,
          keycapData: updatedKeycapData, // Update the keycapData with the modified array
        };
      });
    } else if (selectedColorEntry !== null) {
      // if the user has a color entry selected:
      updateDesign((prevDesign) => {
        const selectedColorEntry =
          prevDesign.keycapColorEditor.selectedColorEntry;

        const colorsLength = prevDesign.keycapColorEditor.colors.length; // get the amount of color entries in the users list
        if (
          selectedColorEntry == null ||
          selectedColorEntry < 0 ||
          selectedColorEntry >= colorsLength
        ) {
          return prevDesign; // safety check (nothing to update if no palette entry is selected or out of bounds)
        }

        // update the color of the selected color entry:
        const updatedColors = [...prevDesign.keycapColorEditor.colors];
        updatedColors[selectedColorEntry] = updatedColor;

        // update the color of every keycap that references this color entry:
        const updatedKeycapData = prevDesign.keycapData.map((row) =>
          row.map((keycap) => {
            if (!keycap || keycap.isSpacer) return keycap; // skip empty objects and spacers

            return keycap.selectedKeycapColorIndex === selectedColorEntry
              ? { ...keycap, color: updatedColor } // Update the color of the keycap
              : keycap; // Return keycap unchanged if it doesn't reference the color entry
          })
        );

        return {
          ...prevDesign,
          keycapColorEditor: {
            ...prevDesign.keycapColorEditor,
            colors: updatedColors, // update the colors array in the keycapColorEditor
          },
          keycapData: updatedKeycapData, // update the keycapData
        };
      });
    }
  }; // this function handles applying a color to all keycaps of a selected group, applies a color to all keycaps that reference the same color index that is changed, or applies a color directly to a specific keycap

  const handleSelectGroup = (val) => {
    updateDesign((prev) => {
      if (val == null || val === 'none') {
        // if the user selects the "Custom" group:
        return {
          ...prev,
          settings: {
            ...prev.settings,
            selectedKeycaps: [], // reset the selectedKeycaps array
            isoEnterSelected: false, // make sure the ISO Enter key is unselected
          },
        };
      }

      const matches = (kc) => {
        if (!kc || kc.isSpacer) return false; // skip empty objects and spacers
        if (val === 'all') return true; // if the group is set to "all", select all keycaps
        return kc.group === val; // return true, if the keycap.group matches the selected group (val)
      }; // this helper function checks if a keycap matches the selected group

      const selectedKeycaps = [];
      prev.keycapData.forEach((row, r) => {
        row?.forEach((kc, k) => {
          // go through all keycaps inside keycapData:
          if (matches(kc))
            selectedKeycaps.push({ rowIndex: r, keycapIndex: k }); // if the current keycap's group matches the selected group, add it to the selectedKeycaps array
        });
      });

      const isIsoEnterSelected = selectedKeycaps.some(
        ({ rowIndex, keycapIndex }) =>
          prev.keycapData?.[rowIndex]?.[keycapIndex]?.isIsoEnter
      ); // check if any of the "new" selected keycaps is an ISO Enter keycap

      return {
        ...prev,
        settings: {
          ...prev.settings,
          selectedKeycaps, // update the selectedKeycaps field
          isoEnterSelected: isIsoEnterSelected, // update the isoEnterSelected field
        },
      };
    });
  }; // this function will select all keycaps of a group, when the user selects one of the predefined groups

  const { hasSelection, isUniform, uniformIndex } = useMemo(() => {
    // gather all color indices across all selectedKeycaps:
    const indices = [];
    for (const { rowIndex, keycapIndex } of selectedKeycaps) {
      // go through all selectedKeycaps
      const k = design.keycapData?.[rowIndex]?.[keycapIndex]; // get the data of the current keycap
      if (k && !k.isSpacer) indices.push(k.selectedKeycapColorIndex ?? null); // add the selectedKeycapColorIndex of the current keycap to the indices array
    }

    const has = indices.length > 0;
    if (!has)
      return { hasSelection: false, isUniform: false, uniformIndex: null }; // if no keycaps are selected (or none of the entries inside selectedKeycaps is valid), set all values to false or null

    const first = indices[0];
    const uniform = indices.every((v) => v === first);
    return {
      hasSelection: true, // selectedKeycaps has at least one entry
      isUniform: uniform, // true, if all selectedKeycaps reference the same color entry from the list
      uniformIndex: uniform ? first : null, // this will hold the index of the color entry from the list, if all selectedKeycaps reference the same color entry
    };
  }, [selectedKeycaps, design.keycapData]); // recomputes hasSelection, isUniform and uniformIndex when selectedKeycaps or keycapData change

  const selectedGroupValue = useMemo(() => {
    const kd = design.keycapData || [];

    const actualSelected = selectedKeycaps.filter(
      ({ rowIndex, keycapIndex }) => {
        const k = kd[rowIndex]?.[keycapIndex];
        return k && !k.isSpacer;
      }
    ); // collect all selected keycaps (skip spacers and empty objects)

    if (actualSelected.length === 0) return 'none'; // if no selected keycaps -> no group

    // 1. count all keycaps (excluding spacer objects) in the whole layout:
    let total = 0;
    for (const row of kd) {
      for (const cap of row || []) {
        if (cap && !cap.isSpacer) total++;
      }
    }

    if (total > 0 && actualSelected.length === total) {
      return 'all'; // if all keycaps are selected -> return "all"
    }

    // 2. check which groups are present in the current selection:
    const groupsInSelection = new Set(
      actualSelected.map(
        ({ rowIndex, keycapIndex }) => kd[rowIndex][keycapIndex].group ?? 'none'
      )
    );

    // 3. must be exactly one group and not "none":
    if (groupsInSelection.size !== 1) return 'none';
    const [groupName] = groupsInSelection;
    if (!groupName || groupName === 'none') return 'none';

    // 4. count how many keycaps exist in this group in the whole layout:
    let totalInGroup = 0;
    for (let r = 0; r < kd.length; r++) {
      const row = kd[r] || [];
      for (let c = 0; c < row.length; c++) {
        const k = row[c];
        if (k && !k.isSpacer && k.group === groupName) totalInGroup++;
      }
    }

    // 5. count how many of those are currently selected:
    const selectedInGroup = actualSelected.filter(
      ({ rowIndex, keycapIndex }) =>
        kd[rowIndex][keycapIndex].group === groupName
    ).length;

    // 6. only return the group name if the selection fully covers that group:
    return totalInGroup > 0 && selectedInGroup === totalInGroup
      ? groupName
      : 'none';
  }, [design.keycapData, selectedKeycaps]); // recomputes selectedGroupValue when selectedKeycaps or keycapData change

  if (!isActive) {
    return null; // Do not render anything if not active. (this is better than conditionally rendering the KeycapColorEditor component in the CustomizerScreen, because now this component doesn't mound and unmount every time the user switches tabs. This cause the useEffect hooks to be triggered for every mount and the states to be set back to defaults whenever it is unmounted)
  }

  return (
    <div className="keycap-color-editor-container">
      <div className="keycap-color-settings-container">
        <Form.Group
          controlId="groupSelect"
          className="customizer-color-group-form"
          id="keycap-group"
        >
          <ToggleButtonGroup
            type="radio"
            name="selectedGroup"
            value={selectedGroupValue ?? 'none'}
            onChange={(val) => handleSelectGroup(val)}
          >
            {keyGroups
              .filter(
                (group) =>
                  relevantGroups.includes(group.value) ||
                  group.value === 'none' ||
                  group.value === 'all'
              )
              .map((group, index) => (
                <ToggleButton
                  key={group.value}
                  id={index}
                  value={group.value}
                  variant="outline-secondary"
                  onClick={() => {
                    if (group.value !== 'none') {
                      // if the user selects a group, reset the selectedColorEntry:
                      updateDesign({
                        'keycapColorEditor.selectedColorEntry': null,
                      });
                    }
                  }}
                >
                  {group.name}
                </ToggleButton>
              ))}
          </ToggleButtonGroup>
        </Form.Group>

        <div className="customizer-colors-container">
          <div
            id="color-picker"
            style={{
              opacity:
                !selectedKeycaps?.length && selectedColorEntry === null
                  ? 0.4
                  : 1, // Dim if disabled
              pointerEvents:
                !selectedKeycaps?.length && selectedColorEntry === null
                  ? 'none'
                  : 'auto', // Disable interactions
            }}
          >
            <ChromePicker
              className="keycap-color-editor-chrome-picker"
              color={
                selectedKeycaps.length > 0
                  ? majorityColor
                  : selectedColorEntry !== null
                    ? colors[selectedColorEntry]
                    : '#FFFFFF'
              }
              onChange={(color) => handleColorChange(color)}
              disableAlpha={true} // no alpha is allowed for keycap colors, as the colors for keycaps must be 100% opaque
            />
          </div>

          <div
            className="keycap-color-editor-color-list-container"
            id="color-list"
          >
            <ColorList
              colors={colors}
              selectedIndex={selectedColorEntry}
              handleColorButtonClick={handleKeycapColorButtonClick}
              handleAddColor={handleAddColor}
              deleteMode={deleteMode}
              toggleDeleteMode={toggleDeleteColorMode}
              keycapSetting={null}
              setting={null}
              currentColor={majorityColor} // the color that is present the most across the current selectedKeycaps (if none selected, uses fallback)
              hasSelection={hasSelection}
              isUniformSelection={isUniform}
              uniformSelectedIndex={uniformIndex}
              showUnselectButton={showUnselectButton}
              unselectKeycaps={unselectKeycaps}
            />
          </div>
        </div>
      </div>

      <div
        className={`keycap-color-randomizer-container ${
          !hasAssignedColorGroups && 'disabled-randomizer-button'
        }`}
      >
        <Button
          className="random-keycap-color-button"
          variant="none"
          onClick={randomizeColors}
          id="color-randomizer"
        >
          <Icon icon="fad:random-1dice"></Icon>
        </Button>
      </div>
    </div>
  );
};

export default KeycapColorEditor;
